import { forwardRef, useMemo, useState } from 'react'
import { LayoutChangeEvent, Pressable, StyleSheet, Text, View } from 'react-native'
import { cva, type VariantProps } from 'class-variance-authority'
import Animated, { Easing, useAnimatedStyle, withTiming, type WithTimingConfig } from 'react-native-reanimated'

import { cn } from '@/utils/cn'

const variants = cva('h-9 flex-row items-center rounded-lg bg-fill-subtle', {
  variants: {
    variant: {
      formInput: 'h-12 border border-primary',
      inline: 'w-[120px]'
    },
    error: {
      true: 'border-red-500'
    },
    disabled: {
      true: 'opacity-50'
    }
  },
  defaultVariants: {
    variant: 'inline',
    error: false,
    disabled: false
  }
})

type SegmentedControlOptions = {
  label: string
  value: string
  hasNew: boolean
}

export interface SegmentedControlProps extends VariantProps<typeof variants> {
  className?: string
  options?: SegmentedControlOptions[]
  value?: string
  onChange?: (value: string) => void
  error?: boolean
  disabled?: boolean
}

const config: WithTimingConfig = { duration: 300, easing: Easing.inOut(Easing.cubic) }

export const SegmentedControl = forwardRef<View, SegmentedControlProps>(
  ({ className, options, value, onChange, variant, error, disabled }, ref) => {
    const [width, setWidth] = useState<number>(0)

    const onLayout = (event: LayoutChangeEvent) => {
      const layoutWidth = event.nativeEvent.layout.width
      if (width !== layoutWidth) {
        setWidth(layoutWidth)
      }
    }

    const offset = useMemo(() => {
      const index = (options ?? []).findIndex((item) => item.value === value)
      return Math.max(0, index * width)
    }, [width, value, options])

    const style = useAnimatedStyle(() => {
      const translateX = withTiming(offset, config)
      return { transform: [{ translateX }] }
    }, [offset])

    return (
      <View ref={ref} className={cn(variants({ variant, error, disabled, className }))}>
        {/* NOTE: `className` is not mapped to styles on Reanimated's animated
            components, so indicator positioning is expressed with `style`. */}
        <Animated.View style={[{ width: width - 8 }, styles.indicator, style]} />
        {options?.map((item, index) => {
          const selected = item.value === value
          return (
            <Pressable
              key={index}
              onPress={() => onChange?.(item.value)}
              className={cn(
                'h-full flex-1 items-center justify-center shadow-1',
                index === 0 && 'rounded-l-lg',
                index === options.length - 1 && 'rounded-r-lg'
              )}
              onLayout={index === 0 ? onLayout : undefined}
              disabled={disabled}
            >
              {({ pressed }) => (
                <>
                  <Text
                    className={cn(
                      'text-base font-bold',
                      pressed && 'opacity-50',
                      selected ? 'text-text-primary' : 'text-text-tertiary'
                    )}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                  {item.hasNew && <View className={'absolute right-[7px] top-[7px] size-1.5 rounded-full bg-danger'} />}
                </>
              )}
            </Pressable>
          )
        })}
      </View>
    )
  }
)

const styles = StyleSheet.create({
  // `absolute inset-y-1 left-1 rounded bg-white` in style form.
  indicator: {
    position: 'absolute',
    top: 4,
    bottom: 4,
    left: 4,
    borderRadius: 4,
    backgroundColor: '#FFFFFF'
  }
})
